import React from 'react';
import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';

import { useModalManagerContext } from './ModalManagerContext';

interface ModalManagerProps {
  config: any;
  isOpen: boolean;
  onClose: () => void;
  type: string;
}

/**
 * ModalManager is a component to manage different type of modals based on a
 * configuration that is passed as a property. It can handle 3 different
 * configuration:
 *
 * - Single Modal:
 *   When you want to just show a modal that doesn't have any steps. For
 *   example, showing a modal to confirm an action, that on both main and
 *   secondary actions finish, will close. An example of this configuration
 *   would be like:
 *
 *   const config = { firstStep: <MyModal /> };
 *
 * - Single Flow Modal:
 *   When you want to show a modal that has steps as a flow, but it only has
 *   one flow. For example, a modal where in the first step you ask the user to
 *   input the address, and in a second step you ask for payment info, and in
 *   the last step you confirm the buy. An example of this configuration would
 *   be like:
 *
 *   const config = {
 *     firstStep: <AddressModal />,
 *     flow: [
 *       <PaymentModal />,
 *       <ConfirmBuyModal />,
 *     ],
 *   };
 *
 * - Multiple Flow Modal:
 *   When, depending on some data from the first step, you need to go to one
 *   or another flow. For example, cancel mitigation process, where depending
 *   on the choice of the user, the flow is completely different. An example of
 *   this configuration would be like:
 *
 *   const configWithFlows = {
 *     firstStep: <ReasonSelectorModal />,
 *     flows: {
 *       BUDGET: [
 *         <DiscountModal />,
 *         <ConfirmCancellationModal />,
 *       ],
 *       TOO_MUCH: [
 *         <FrequencyEditorStep1Modal />,
 *         <FrequencyEditorStep2Modal />,
 *         <ConfirmCancellationModal />,
 *       ],
 *       TRAVELLING: [<ConfirmCancellationModal />],
 *     },
 *   };
 */
const ModalManager: React.FC<ModalManagerProps> = ({
  config,
  isOpen,
  onClose,
  type,
}) => {
  const { firstStepShown, flowIndex, flowKey } = useModalManagerContext();

  const getModalContent = () => {
    if (firstStepShown || !config.flows) {
      return config.firstStep;
    }

    if (Array.isArray(config.flows) && config.flows[flowIndex]) {
      return config.flows[flowIndex];
    }

    if (config.flows[flowKey] && config.flows[flowKey][flowIndex]) {
      return config.flows[flowKey][flowIndex];
    }

    throw new Error(
      `Config flow is not valid for flow ${flowKey} at index ${flowIndex}`,
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay className={`awt-${type}-modal--overlay`}>
        <ModalContent className={`awt-${type}-modal`}>
          {getModalContent()}
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default ModalManager;
